import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import type {
  ConnectorAccessView,
  ConnectorAvailabilityView,
  PutConnectorAccessBody,
  PutPlatformConnectorAccessBody,
} from "@shared/types/connectorAccess";
import { CONNECTORS_CATALOG_PATH } from "@/hooks/use-connectors";
import { apiRequest } from "@/lib/api";

/**
 * Which connectors a NEW connection may use (T-51, round 8; `shared/types/connectorAccess.ts`):
 * - the tenant's choice, `GET|PUT /api/v2/tenants/:id/connector-access` (`tenant.instances.manage`);
 * - the same, set or locked from Tenants, `/api/v2/platform/tenants/:id/connector-access`
 *   (`platform.tenants.manage`);
 * - connectors made Not available for every tenant, `/api/v2/platform/connector-availability`
 *   (`platform.connectors.manage`).
 * Every save refreshes the connector catalog, whose `offer` the New connection picker reads.
 */

export function connectorAccessPath(tenantId: string): string {
  return `/api/v2/tenants/${tenantId}/connector-access`;
}

export function platformConnectorAccessPath(tenantId: string): string {
  return `/api/v2/platform/tenants/${tenantId}/connector-access`;
}

export const CONNECTOR_AVAILABILITY_PATH = "/api/v2/platform/connector-availability";

export function useConnectorAccess(
  tenantId: string | null,
  enabled = true,
): UseQueryResult<ConnectorAccessView> {
  return useQuery<ConnectorAccessView>({
    queryKey: [tenantId ? connectorAccessPath(tenantId) : "connector-access-disabled"],
    enabled: enabled && Boolean(tenantId),
  });
}

export function useSaveConnectorAccess(tenantId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PutConnectorAccessBody) =>
      apiRequest<ConnectorAccessView>(connectorAccessPath(tenantId ?? ""), {
        method: "PUT",
        body,
        // #1110: which connectors a tenant may use needs a recent sign-in to change.
        stepUp: "change",
      }),
    onSuccess: (view) => {
      client.setQueryData([connectorAccessPath(view.tenantId)], view);
      void client.invalidateQueries({ queryKey: [CONNECTORS_CATALOG_PATH] });
    },
  });
}

export function usePlatformConnectorAccess(
  tenantId: string | null,
): UseQueryResult<ConnectorAccessView> {
  return useQuery<ConnectorAccessView>({
    queryKey: [tenantId ? platformConnectorAccessPath(tenantId) : "platform-connector-access-off"],
    enabled: Boolean(tenantId),
  });
}

export function useSavePlatformConnectorAccess(tenantId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PutPlatformConnectorAccessBody) =>
      apiRequest<ConnectorAccessView>(platformConnectorAccessPath(tenantId ?? ""), {
        method: "PUT",
        body,
        stepUp: "change",
      }),
    onSuccess: (view) => {
      client.setQueryData([platformConnectorAccessPath(view.tenantId)], view);
      void client.invalidateQueries({ queryKey: [connectorAccessPath(view.tenantId)] });
      void client.invalidateQueries({ queryKey: [CONNECTORS_CATALOG_PATH] });
    },
  });
}

export function useConnectorAvailability(
  enabled = true,
): UseQueryResult<ConnectorAvailabilityView> {
  return useQuery<ConnectorAvailabilityView>({
    queryKey: [CONNECTOR_AVAILABILITY_PATH],
    enabled,
  });
}

export function useSetConnectorAvailability() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ name, available }: { name: string; available: boolean }) =>
      apiRequest<ConnectorAvailabilityView>(
        `${CONNECTOR_AVAILABILITY_PATH}/${encodeURIComponent(name)}`,
        // #1110: the platform switch needs a recent sign-in.
        { method: "PUT", body: { available }, stepUp: "change" },
      ),
    onSuccess: (view) => {
      client.setQueryData([CONNECTOR_AVAILABILITY_PATH], view);
      void client.invalidateQueries({ queryKey: [CONNECTORS_CATALOG_PATH] });
    },
  });
}
